import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import api from 'shared/services/api';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router';
import useFetch from 'shared/hooks/useFetch';
import { useForm } from '@mantine/form';
import { useTranslations } from 'shared/translations/useTranslations';
import { get } from 'lodash';
import {
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Notification,
  Select,
  Textarea,
} from '@mantine/core';
import { X as IconX } from 'tabler-icons-react';

interface DeviceActionModel {
  id: number;
  name: string;
  amount: string;
  note: string;
  createdAt: Date;
}

interface Props {
  deviceId?: string | number;
  actionId?: string | number;
  refreshParent: () => any;
  onClose: () => any;
}

interface Data {
  action: DeviceActionModel;
}

const DeleteActionModal = (props: Props) => {
  const { deviceId, actionId, refreshParent, onClose } = props;
  const translations = useTranslations();
  const { data, isLoading, isLoaded, error, refresh } = useFetch<Data>({
    initialValue: { action: {} },
    shouldNotFetchAtStart: true,
    fetchAction: () => api({}).get(`/api/v3/devices/${deviceId}/actions/${actionId}`),
  });

  const [isSaving, setSaving] = useState(false);
  const history = useHistory();

  const handleSubmit = async (formValues: any) => {
    setSaving(true);
    try {
      await api({}).delete(`/api/v3/devices/${deviceId}/actions/${actionId}`);
      toast.success(translations.devices.deleteActionSuccess);
      setSaving(false);
      setOpened(false);
      onClose();
      refreshParent();
    } catch (e) {
      setSaving(false);
      toast.error(translations.global.somethingWentWrong);
    }
  };
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    setOpened(!!actionId);
    if (actionId) {
      refresh();
    }
  }, [actionId]);

  const handleClose = () => {
    setOpened(false);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={translations.devices.deleteActionTitle}>
      <LoadingOverlay visible={isLoading} />
      {error && (
        <Notification icon={<IconX size={18} />} color="red" mt={60} mb={60} disallowClose>
          {translations.global.somethingWentWrong}
        </Notification>
      )}
      {!error && isLoaded && <>{translations.devices.deleteActionDesc}</>}
      <Group position="right" mt={20}>
        <Button
          color="primary"
          loading={isSaving}
          type="button"
          onClick={handleSubmit}
          disabled={!!error || !isLoaded}>
          {translations.global.delete}
        </Button>
        <Button variant="outline" onClick={handleClose}>
          {translations.global.cancel}
        </Button>
      </Group>
    </Modal>
  );
};

export default DeleteActionModal;
